import { useEffect, useState, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Search } from 'lucide-react';
import ScrollReveal from '@/components/animations/ScrollReveal';
import { tests } from '@/data/tests';
import { useLanguage } from '@/hooks/useLanguage';
import { useContent } from '@/hooks/useContent';

gsap.registerPlugin(ScrollTrigger);

const Tests = () => {
  const { t } = useContent();
  const { language } = useLanguage();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    document.title = 'Test Database - ElhamyLabs';
  }, []);

  // Dynamically generate categories from tests data
  const uniqueCategories = Array.from(
    new Set(tests.map(test => test.category[language]))
  ).sort();

  const categories = [
    { key: 'all', label: language === 'ar' ? 'الكل' : 'All' },
    ...uniqueCategories.map(cat => ({
      key: cat,
      label: cat,
    })),
  ];

  const filteredTests = tests.filter((test) => {
    const matchesSearch = test.name[language].toLowerCase().includes(searchQuery.toLowerCase()) ||
      test.description[language].toLowerCase().includes(searchQuery.toLowerCase()) ||
      test.shortcutName.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || test.category[language] === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-brand-purple-dark via-brand-purple-base to-brand-purple-light dark:from-dark-primary dark:via-dark-secondary">
        <div className="container mx-auto px-4 text-center">
          <ScrollReveal>
            <h1 className="text-5xl md:text-7xl font-bold mb-6 text-white">
              {t('common.tests')}
            </h1>
            <p className="text-xl text-white/90 max-w-3xl mx-auto">
              {t('tests.subtitle')}
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* Search & Filter Section */}
      <section ref={sectionRef} className="py-8 bg-light-secondary dark:bg-dark-secondary sticky top-20 z-40 shadow-sm">
        <div className="container mx-auto px-4">
          {/* Search Input - Standalone Row */}
          <div className="mb-6">
            <div className="max-w-2xl mx-auto relative">
              <Search className="absolute left-5 top-1/2 transform -translate-y-1/2 text-light-text-secondary dark:text-dark-text-secondary z-10" size={22} />
              <input
                type="text"
                placeholder={t('tests.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-14 pr-5 py-4 text-base rounded-xl bg-light-primary dark:bg-dark-primary text-light-text dark:text-dark-text border-2 border-light-text/20 dark:border-dark-text/20 shadow-md hover:shadow-lg focus:shadow-xl focus:outline-none focus:border-brand-purple-base dark:focus:border-brand-orange-base transition-all duration-300 placeholder:text-light-text-secondary/60 dark:placeholder:text-dark-text-secondary/60"
              />
            </div>
          </div>
          
          {/* Category Filters - Separate Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
            {categories.map((category) => (
              <button
                key={category.key}
                onClick={() => setSelectedCategory(category.key)}
                className={`px-3 py-2 rounded-lg text-sm text-center transition-all duration-200 ${
                  selectedCategory === category.key
                    ? 'bg-brand-orange-base text-white shadow-md scale-105'
                    : 'bg-light-primary dark:bg-dark-primary text-light-text dark:text-dark-text hover:bg-light-accent dark:hover:bg-dark-accent hover:shadow-sm'
                }`}
              >
                {category.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Tests Grid */}
      <section className="py-20 bg-light-primary dark:bg-dark-primary">
        <div className="container mx-auto px-4">
          {filteredTests.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-xl text-light-text-secondary dark:text-dark-text-secondary">
                {t('tests.noTestsFound')}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredTests.map((test, index) => (
                <ScrollReveal key={test.id} delay={index * 0.05}>
                  <div className="p-6 bg-light-secondary dark:bg-dark-secondary rounded-xl hover:shadow-xl transition-all duration-300">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="text-xl font-bold text-light-text dark:text-dark-text">
                            {test.name[language]}
                          </h3>
                          <span className="px-2 py-1 text-xs font-semibold rounded-md bg-brand-purple-base dark:bg-brand-orange-base text-white">
                            {test.shortcutName}
                          </span>
                        </div>
                        <span className="text-sm text-brand-purple-base dark:text-brand-orange-base">
                          {test.category[language]}
                        </span>
                      </div>
                    </div>
                    <p className="text-light-text-secondary dark:text-dark-text-secondary mb-4">
                      {test.description[language]}
                    </p>
                    <div className="space-y-2 text-sm text-light-text-secondary dark:text-dark-text-secondary">
                      <div>
                        <strong>{t('common.preparation')}:</strong> {test.preparation[language]}
                      </div>
                      <div>
                        <strong>{t('common.turnaround')}:</strong> {test.turnaroundTime[language]}
                      </div>
                    </div>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Tests;

